import { Button } from '@ctw/shared/components/button';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { TelemetryBoundary } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { isReactNode } from '@ctw/shared/utils/types';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faAngleRight, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash-es';
import type { ReactNode } from 'react';

export interface OverviewCardProps {
  title: string;
  children: ReactNode;
  testId?: string;
  className?: ClassName;
  loading?: boolean;
  empty?: boolean;
  fullbleed?: boolean;
  emptyState?: ReactNode;
  fixedHeightScrollable?: boolean;
  helpText?: string;
  headerIcon?: IconProp | ReactNode;
  footerCTA?:
    | {
        label: string;
        onClick: () => void;
      }
    | undefined;
}

export const OverviewCard = ({
  children,
  className,
  footerCTA,
  headerIcon,
  loading,
  empty = false,
  fullbleed,
  emptyState,
  fixedHeightScrollable = true,
  title,
  helpText,
  testId,
}: OverviewCardProps) => (
  <TelemetryBoundary boundaryName="OverviewCard" initialContext={{ overviewCardTitle: title }}>
    {({ telemetry }) => (
      <section
        data-testid={testId}
        className={twx(
          'flex min-h-[175px] min-w-[320px] flex-col divide-y divide-solid divide-border-main overflow-visible rounded-lg border border-border-main border-solid shadow',
          { '@2xl/cards:max-h-[380px]': fixedHeightScrollable },
          className,
        )}
      >
        <div
          className={tw`min-height-[44px] flex h-[44px] max-h-[44px] w-full items-center justify-between space-x-2 rounded-t-lg px-3 py-2.5`}
        >
          {headerIcon && (
            <div>
              {isReactNode(headerIcon) ? (
                headerIcon
              ) : (
                <FontAwesomeIcon icon={headerIcon} className={tw`h-5 w-5 text-content-icon`} />
              )}
            </div>
          )}
          <div
            data-testid="overview-card-header"
            className={tw`flex-auto font-medium text-base text-content-main leading-normal`}
          >
            {title}
          </div>
          {helpText && (
            <div className={tw`self-center`}>
              <Tooltip id="overview-card-header-help-icon" content={helpText}>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className={tw`block h-4 w-4 text-content-icon`}
                />
              </Tooltip>
            </div>
          )}
        </div>

        <div
          data-testid="overview-card-body"
          className={twx({
            'flex grow items-center justify-center overflow-x-visible px-3 py-2 text-center text-sm':
              loading || empty,
            'scroll-shadows flex w-full flex-1 grow flex-col overflow-y-auto py-0 text-sm':
              !loading,
            'px-3 py-2': !fullbleed,
            // Round the body's bottom only when no footer follows it; when a footer is shown (now
            // including the loading state, e.g. a hotload-gated card) the footer owns the rounding.
            'rounded-b-lg': !footerCTA,
          })}
          onScroll={debounce(
            () => {
              telemetry.trackInteraction('overview_card_scroll');
            },
            500,
            { leading: false, trailing: true },
          )}
        >
          {loading ? (
            <LoadingSpinner message="Loading..." />
          ) : (
            empty && (
              <div className={tw`px-10 py-4 font-normal text-content-main text-sm leading-tight`}>
                {emptyState ?? 'No data'}
              </div>
            )
          )}
          {!(loading || empty) && children}
        </div>

        {footerCTA && (
          <Button
            testId="overview-card-footer"
            type="button"
            variant="unstyled"
            className={tw`group flex min-h-[40px] w-full cursor-pointer items-center justify-between rounded-b-lg bg-white px-3 py-2 text-left font-semibold text-content-subtle text-sm leading-tight hover:bg-background-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-main`}
            telemetryInteractionName="overview_card_footer_cta_click"
            onClick={footerCTA.onClick}
          >
            {footerCTA.label}
            <FontAwesomeIcon
              icon={faAngleRight}
              className={tw`button-arrow h-5 w-5 text-content-icon opacity-0 group-hover:opacity-100`}
            />
          </Button>
        )}
      </section>
    )}
  </TelemetryBoundary>
);
